import { createBrowserRouter, Navigate } from 'react-router'
import { AppShell } from '@/layouts/AppShell'
import { RequireAuth } from './guards/RequireAuth'
import { RequirePermission } from './guards/RequirePermission'
import {
  AcceptInvitePage, ForgotPasswordPage, LoginPage, ResetPasswordPage,
} from '@/features/auth'
import { DashboardPage } from '@/features/dashboard'
import { NotFoundPage, ForbiddenPage } from '@/features/misc'
import {
  PlatformAuditPage, PlatformBillingPage, PlatformLoginPage, PlatformNewTenantPage,
  PlatformOperatorsPage, PlatformPlansPage, PlatformShell,
  PlatformTenantDetailPage, PlatformTenantsPage, RequireOperator,
} from '@/features/platform'
import {
  AssetCategoriesPage, AssetDetailPage, AssetRegisterPage, AssetReportsPage, AssetStocktakePage,
} from '@/features/asset'
import {
  DocumentLibraryPage, LibraryDocumentPage, LibrarySettingsPage, PolicyCampaignsPage,
} from '@/features/library'
import {
  HelpdeskQueuePage, HelpdeskSetupPage, KnowledgeBasePage, TicketDetailPage,
} from '@/features/helpdesk'
import {
  AnnouncementsPage, RecognitionPage, SurveyResultsPage, SurveysPage,
} from '@/features/engagement'
import {
  AttendanceDevicesPage, AttendanceReportPage, DailyAttendancePage, GeofencingPage, MyPunchPage,
  RegularisationPage, RosterPage,
} from '@/features/attendance'
import { ProjectsPage, TimesheetApprovalsPage, TimesheetPage } from '@/features/timesheet'
import {
  AddEmployeePage, BenefitDetailPage, EmployeeAssignmentsPage, EmployeeDirectoryPage,
  EmployeeProfilePage,
  OrgChartPage,
  TransfersPage,
} from '@/features/employee'
import { LeaveBalancesPage, LeavePage, LeaveTypesPage } from '@/features/leave'
import {
  AccessLogPage, BrandingPage, CustomFieldsPage, EntityGrantsPage, SegregationOfDutiesPage,
} from '@/features/admin'
import {
  DepartmentsPage, DivisionsPage, EstablishmentsPage, OrgMastersPage, PositionsPage,
} from '@/features/org'
import { CompanyDocumentsPage, DocumentTypesPage, ExpiringDocumentsPage } from '@/features/document'
import {
  ComplianceCalendarPage, ComplianceItemsPage, CredentialRulesPage, DataRequestsPage,
  FilingLogsPage, NationalisationPage, RetentionReviewPage,
} from '@/features/compliance'
import { ApprovalsPage } from '@/features/approvals'
import {
  LetterTemplatesPage, MyRequestsPage, RequestTypesPage, RequestsPage,
} from '@/features/request'
import { ProBoardPage, ProCasePage, ProReportsPage, ProTemplatesPage } from '@/features/pro'
import {
  PayElementsPage, PayrollRunPage, PayrollRunsPage, ProvisionsPage,
  MyPayslipsPage,
} from '@/features/payroll'
import {
  BenefitsPage, ChartOfAccountsPage, PayBandsPage, SalaryRevisionsPage,
} from '@/features/compensation'
import { LoanDetailPage, LoanTypesPage, LoansPage } from '@/features/loan'
import {
  ExpenseClaimDetailPage, ExpenseClaimsPage, ExpenseTypesPage, PerDiemRatesPage,
  TravelRequestsPage,
} from '@/features/expense'
import {
  PaymentBatchesPage, PayrollReportsPage, SettlementDetailPage, SettlementTermsPage,
  SettlementsPage, WpsLayoutsPage,
} from '@/features/uae'
import {
  CandidatesPage, OffersPage, RecruiterDashboardPage, RecruitmentSettingsPage,
  RequisitionDetailPage, RequisitionsPage,
} from '@/features/recruitment'
import {
  AppraisalCycleDetailPage, AppraisalCyclesPage, CalibrationPage, GoalsPage,
  PerformanceSetupPage, PipsPage,
} from '@/features/performance'
import { AiSettingsPage } from '@/features/ai'
import { ExitCaseDetailPage, ExitCasesPage, ExitSettingsPage } from '@/features/exit'
import { ReportBuilderPage, ReportCataloguePage } from '@/features/reporting'
import {
  BankMasterPage, DataMigrationPage, NotificationSettingsPage, SettingsHubPage, WorkflowsPage,
} from '@/features/settings'
import {
  AuditLogPage, RolesPage, SessionsPage, SystemHealthPage, UsersPage,
} from '@/features/sysadmin'
import {
  ApiClientsPage, ErpExportPage, SignaturesPage, SsoPage, WebhooksPage,
} from '@/features/integrations'
import { DelegationPage, MyTeamPage, PortalPage } from '@/features/ess'
import { KiosksPage, MyDevicesPage } from '@/features/mobile'
import { BankChannelsPage } from '@/features/banking'
import {
  TrainingBudgetsPage, TrainingCataloguePage, TrainingNeedsPage, TrainingSessionsPage,
} from '@/features/training'
import { BiAccessPage } from '@/features/bi'
import { ReportExportsPage } from '@/features/exports'
import { ProfilePage } from '@/features/profile'

/**
 * Route tree.
 *
 * Every sidebar entry has a real, permission-guarded route from Phase 2 — modules that
 * arrive later render a placeholder rather than being absent, so navigation, guards and
 * the active-nav state are exercised end to end now.
 */
export const router = createBrowserRouter([
  { path: '/login', element: <LoginPage /> },
  { path: '/accept-invite', element: <AcceptInvitePage /> },
  { path: '/forgot-password', element: <ForgotPasswordPage /> },
  { path: '/reset-password', element: <ResetPasswordPage /> },
  { path: '/forbidden', element: <ForbiddenPage /> },

  // The operator console.
  //
  // Outside RequireAuth on purpose, and not merely alongside it: these pages belong to the
  // installation rather than to any customer, they authenticate against a different realm
  // with a different token, and RequireOperator reads a different store. A signed-in tenant
  // user who types this URL is sent to the operator sign-in, not to their own dashboard —
  // and the API would refuse them anyway, because the two token realms do not overlap.
  { path: '/platform/login', element: <PlatformLoginPage /> },
  {
    path: '/platform',
    element: <RequireOperator />,
    children: [
      {
        element: <PlatformShell />,
        children: [
          { index: true, element: <Navigate to="/platform/tenants" replace /> },
          { path: 'tenants', element: <PlatformTenantsPage /> },
          { path: 'tenants/new', element: <PlatformNewTenantPage /> },
          { path: 'tenants/:id', element: <PlatformTenantDetailPage /> },
          { path: 'plans', element: <PlatformPlansPage /> },
          { path: 'billing', element: <PlatformBillingPage /> },
          { path: 'operators', element: <PlatformOperatorsPage /> },
          { path: 'audit', element: <PlatformAuditPage /> },
        ],
      },
    ],
  },

  {
    element: <RequireAuth />,
    children: [
      {
        element: <AppShell />,
        children: [
          { index: true, element: <DashboardPage /> },
          // The inbox is not permission-gated: what somebody may decide is decided by the
          // task being assigned to them, and the engine enforces that.
          { path: 'approvals', element: <ApprovalsPage /> },
          { path: 'profile', element: <ProfilePage /> },
          { path: 'requests/my', element: <MyRequestsPage /> },

          {
            element: <RequirePermission permission="employee:read" />,
            children: [
              { path: 'employees', element: <EmployeeDirectoryPage /> },
              { path: 'employees/new', element: <AddEmployeePage /> },
              { path: 'employees/org-chart', element: <OrgChartPage /> },
              { path: 'employees/transfers', element: <TransfersPage /> },
              { path: 'employees/:id', element: <EmployeeProfilePage /> },
              { path: 'employees/:id/assignments', element: <EmployeeAssignmentsPage /> },
              // The tiles on the Gratuity & tickets tab showed a closing balance and nothing
              // else; "why is it that number" had no answer anywhere. This is that answer.
              { path: 'employees/:id/benefits/:type', element: <BenefitDetailPage /> },
              { path: 'employees/:id/documents', element: <EmployeeAssignmentsPage /> },
              { path: 'org/departments', element: <DepartmentsPage /> },
              { path: 'org/divisions', element: <DivisionsPage /> },
              { path: 'org/establishments', element: <EstablishmentsPage /> },
              { path: 'org/masters', element: <OrgMastersPage /> },
              // Its own screen, not the first tab of a page called "Org masters": nobody
              // looks for the companies they employ people through under that name. Same
              // component, pinned to one master, so the two cannot drift apart.
              { path: 'org/legal-entities', element: <OrgMastersPage only="entities" /> },
              { path: 'org/positions', element: <PositionsPage /> },
            ],
          },
          {
            element: <RequirePermission permission="exit:read" />,
            children: [
              { path: 'exit', element: <ExitCasesPage /> },
              { path: 'exit/settings', element: <ExitSettingsPage /> },
              { path: 'exit/:id', element: <ExitCaseDetailPage /> },
            ],
          },
          {
            element: <RequirePermission permission="compliance:read" />,
            children: [
              { path: 'compliance', element: <ComplianceCalendarPage /> },
              { path: 'compliance/items', element: <ComplianceItemsPage /> },
              { path: 'compliance/nationalisation', element: <NationalisationPage /> },
              { path: 'compliance/filings', element: <FilingLogsPage /> },
              { path: 'compliance/retention', element: <RetentionReviewPage /> },
              { path: 'compliance/requests', element: <DataRequestsPage /> },
            ],
          },
          {
            element: <RequirePermission permission="recruitment:read" />,
            children: [
              { path: 'recruitment', element: <RecruiterDashboardPage /> },
              { path: 'recruitment/requisitions', element: <RequisitionsPage /> },
              { path: 'recruitment/requisitions/:id', element: <RequisitionDetailPage /> },
              { path: 'recruitment/candidates', element: <CandidatesPage /> },
              { path: 'recruitment/offers', element: <OffersPage /> },
              { path: 'recruitment/settings', element: <RecruitmentSettingsPage /> },
            ],
          },
          {
            element: <RequirePermission permission="attendance:read" />,
            children: [
              { path: 'attendance', element: <Navigate to="/attendance/daily" replace /> },
              { path: 'attendance/daily', element: <DailyAttendancePage /> },
              { path: 'attendance/roster', element: <RosterPage /> },
              { path: 'attendance/holidays', element: <RosterPage /> },
              { path: 'attendance/regularisation', element: <RegularisationPage /> },
              { path: 'attendance/devices', element: <AttendanceDevicesPage /> },
              { path: 'attendance/sources', element: <AttendanceDevicesPage /> },
              { path: 'attendance/feed', element: <AttendanceDevicesPage /> },
              { path: 'attendance/reports', element: <AttendanceReportPage /> },
            ],
          },
          {
            element: <RequirePermission permission="leave:read" />,
            children: [
              { path: 'leave', element: <LeavePage /> },
              { path: 'leave/balances', element: <LeaveBalancesPage /> },
              { path: 'leave/types', element: <LeaveTypesPage /> },
            ],
          },
          {
            element: <RequirePermission permission="timesheet:read" />,
            children: [
              { path: 'timesheets', element: <TimesheetPage /> },
              { path: 'timesheets/projects', element: <ProjectsPage /> },
              { path: 'timesheets/approvals', element: <TimesheetApprovalsPage /> },
            ],
          },
          {
            // Self-service: payroll:read is what every employee holds, and this is what it opens.
            element: <RequirePermission permission="payroll:read" />,
            children: [
              { path: 'payroll/my-payslips', element: <MyPayslipsPage /> },
            ],
          },
          {
            element: <RequirePermission permission="payroll:read-all" />,
            children: [
              { path: 'payroll', element: <Navigate to="/payroll/runs" replace /> },
              { path: 'payroll/runs', element: <PayrollRunsPage /> },
              { path: 'payroll/runs/:id', element: <PayrollRunPage /> },
              { path: 'payroll/elements', element: <PayElementsPage /> },
              { path: 'payroll/provisions', element: <ProvisionsPage /> },
              { path: 'payroll/chart-of-accounts', element: <ChartOfAccountsPage /> },
            ],
          },
          {
            element: <RequirePermission permission="compensation:read" />,
            children: [
              { path: 'compensation', element: <Navigate to="/compensation/bands" replace /> },
              { path: 'compensation/bands', element: <PayBandsPage /> },
              { path: 'compensation/revisions', element: <SalaryRevisionsPage /> },
            ],
          },
          {
            element: <RequirePermission permission="benefit:read" />,
            children: [{ path: 'benefits', element: <BenefitsPage /> }],
          },
          {
            element: <RequirePermission permission="performance:read" />,
            children: [
              { path: 'performance', element: <Navigate to="/performance/training-needs" replace /> },
              { path: 'performance/training-needs', element: <TrainingNeedsPage /> },
              { path: 'training/catalogue', element: <TrainingCataloguePage /> },
              { path: 'training/sessions', element: <TrainingSessionsPage /> },
              { path: 'training/budgets', element: <TrainingBudgetsPage /> },
              { path: 'performance/appraisals', element: <AppraisalCyclesPage /> },
              { path: 'performance/cycles/:id', element: <AppraisalCycleDetailPage /> },
              { path: 'performance/goals', element: <GoalsPage /> },
              { path: 'performance/calibration', element: <CalibrationPage /> },
              { path: 'performance/plans', element: <PipsPage /> },
              { path: 'performance/setup', element: <PerformanceSetupPage /> },
            ],
          },
          {
            element: <RequirePermission permission="loan:read" />,
            children: [
              { path: 'loans', element: <LoansPage /> },
              { path: 'loans/types', element: <LoanTypesPage /> },
              { path: 'loans/:id', element: <LoanDetailPage /> },
            ],
          },
          {
            element: <RequirePermission permission="expense:read" />,
            children: [
              { path: 'expenses/claims', element: <ExpenseClaimsPage /> },
              { path: 'expenses/claims/:id', element: <ExpenseClaimDetailPage /> },
              { path: 'expenses/types', element: <ExpenseTypesPage /> },
            ],
          },
          {
            element: <RequirePermission permission="travel:read" />,
            children: [
              { path: 'travel/requests', element: <TravelRequestsPage /> },
              { path: 'travel/per-diem', element: <PerDiemRatesPage /> },
            ],
          },
          {
            element: <RequirePermission permission="payment:read" />,
            children: [
              { path: 'payroll/wps', element: <WpsLayoutsPage /> },
              { path: 'payroll/payments', element: <PaymentBatchesPage /> },
            ],
          },
          {
            element: <RequirePermission permission="payroll:read-all" />,
            children: [
              { path: 'payroll/reports', element: <PayrollReportsPage /> },
            ],
          },
          {
            element: <RequirePermission permission="settlement:read" />,
            children: [
              { path: 'settlements', element: <SettlementsPage /> },
              { path: 'settlements/:id', element: <SettlementDetailPage /> },
              { path: 'settings/settlement-terms', element: <SettlementTermsPage /> },
            ],
          },
          {
            element: <RequirePermission permission="asset:read" />,
            children: [
              { path: 'assets', element: <AssetRegisterPage /> },
              { path: 'assets/categories', element: <AssetCategoriesPage /> },
              { path: 'assets/stocktakes', element: <AssetStocktakePage /> },
              { path: 'assets/reports', element: <AssetReportsPage /> },
              { path: 'assets/:id', element: <AssetDetailPage /> },
            ],
          },
          {
            element: <RequirePermission permission="document:read" />,
            children: [
              { path: 'documents', element: <Navigate to="/documents/expiring" replace /> },
              { path: 'documents/expiring', element: <ExpiringDocumentsPage /> },
              { path: 'documents/company', element: <CompanyDocumentsPage /> },
              { path: 'documents/types', element: <DocumentTypesPage /> },
              { path: 'documents/credential-rules', element: <CredentialRulesPage /> },
              { path: 'documents/letters', element: <LetterTemplatesPage /> },
              { path: 'library', element: <DocumentLibraryPage /> },
              { path: 'library/policies', element: <PolicyCampaignsPage /> },
              { path: 'library/setup', element: <LibrarySettingsPage /> },
              { path: 'library/:id', element: <LibraryDocumentPage /> },
            ],
          },
          {
            element: <RequirePermission permission="helpdesk:read" />,
            children: [
              { path: 'helpdesk', element: <HelpdeskQueuePage /> },
              { path: 'helpdesk/knowledge-base', element: <KnowledgeBasePage /> },
              { path: 'helpdesk/setup', element: <HelpdeskSetupPage /> },
              { path: 'helpdesk/:id', element: <TicketDetailPage /> },
            ],
          },
          {
            element: <RequirePermission permission="engagement:read" />,
            children: [
              { path: 'engagement', element: <Navigate to="/engagement/announcements" replace /> },
              { path: 'engagement/announcements', element: <AnnouncementsPage /> },
              { path: 'engagement/surveys', element: <SurveysPage /> },
              { path: 'engagement/surveys/:id', element: <SurveyResultsPage /> },
              { path: 'engagement/recognition', element: <RecognitionPage /> },
            ],
          },
          {
            element: <RequirePermission permission="report:read" />,
            children: [
              { path: 'reports', element: <Navigate to="/reports/exports" replace /> },
              { path: 'reports/exports', element: <ReportExportsPage /> },
              { path: 'reports/bi-access', element: <BiAccessPage /> },
              // The saved-report catalogue — building a report rather than scheduling one of
              // the built-in ones — is still Phase 8.
              { path: 'reports/catalogue', element: <ReportCataloguePage /> },
              { path: 'reports/builder', element: <ReportBuilderPage /> },
            ],
          },
          {
            element: <RequirePermission permission="settings:read" />,
            children: [
              { path: 'settings', element: <SettingsHubPage /> },
              { path: 'settings/notifications', element: <NotificationSettingsPage /> },
              { path: 'settings/banks', element: <BankMasterPage /> },
              { path: 'settings/data-migration', element: <DataMigrationPage /> },
              { path: 'settings/branding', element: <BrandingPage /> },
              { path: 'settings/grants', element: <EntityGrantsPage /> },
              { path: 'settings/custom-fields', element: <CustomFieldsPage /> },
              { path: 'settings/ai', element: <AiSettingsPage /> },
              // The screen was called "document extraction" before it also configured the
              // assistant. Links to the old path are in bookmarks, in the deploy runbook and
              // in two e2e specs, so it redirects rather than 404s.
              { path: 'settings/document-extraction', element: <Navigate to="/settings/ai" replace /> },
              { path: 'settings/segregation-of-duties', element: <SegregationOfDutiesPage /> },
            ],
          },
          {
            // Machine credentials and webhooks: who may read this system, and who it tells.
            element: <RequirePermission permission="dashboard:view" />,
            children: [
              // "Me" is not an administrative area, so it sits behind the permission everybody
              // who can sign in already holds.
              { path: 'portal', element: <PortalPage /> },
              { path: 'portal/my-team', element: <MyTeamPage /> },
              // Signing your own devices out needs no permission beyond being signed in: a
              // person who cannot see where their account is logged in cannot notice that it
              // is logged in somewhere they have never been.
              { path: 'portal/devices', element: <MyDevicesPage /> },
            ],
          },
          {
            element: <RequirePermission permission="delegation:manage" />,
            children: [
              { path: 'portal/delegation', element: <DelegationPage /> },
            ],
          },
          {
            element: <RequirePermission permission="attendance:write" />,
            children: [
              { path: 'attendance/kiosks', element: <KiosksPage /> },
            ],
          },
          {
            // Reading the set-up is attendance:read-all; the writes inside are gated again on
            // the server by the attendance administrator's pair.
            element: <RequirePermission permission="attendance:read-all" />,
            children: [
              { path: 'attendance/geofencing', element: <GeofencingPage /> },
            ],
          },
          {
            element: <RequirePermission permission="attendance:punch-self" />,
            children: [
              { path: 'portal/punch', element: <MyPunchPage /> },
            ],
          },
          {
            element: <RequirePermission permission="payment:bank-channel" />,
            children: [
              { path: 'payroll/bank-channels', element: <BankChannelsPage /> },
            ],
          },
          {
            element: <RequirePermission permission="integration:read" />,
            children: [
              { path: 'settings/api-clients', element: <ApiClientsPage /> },
              { path: 'settings/webhooks', element: <WebhooksPage /> },
              { path: 'settings/sso', element: <SsoPage /> },
            ],
          },
          {
            element: <RequirePermission permission="erp:export" />,
            children: [
              { path: 'settings/erp-export', element: <ErpExportPage /> },
            ],
          },
          {
            element: <RequirePermission permission="esign:read" />,
            children: [
              { path: 'documents/signatures', element: <SignaturesPage /> },
            ],
          },
          {
            element: <RequirePermission permission="admin:system" />,
            children: [
              { path: 'administration', element: <Navigate to="/administration/users" replace /> },
              { path: 'administration/users', element: <UsersPage /> },
              { path: 'administration/roles', element: <RolesPage /> },
              { path: 'administration/sessions', element: <SessionsPage /> },
              { path: 'administration/access-log', element: <AccessLogPage /> },
              { path: 'administration/audit-log', element: <AuditLogPage /> },
              { path: 'administration/workflows', element: <WorkflowsPage /> },
              { path: 'administration/health', element: <SystemHealthPage /> },
            ],
          },

          {
            element: <RequirePermission permission="request:read" />,
            children: [
              { path: 'requests', element: <RequestsPage /> },
              { path: 'requests/types', element: <RequestTypesPage /> },
            ],
          },
          {
            element: <RequirePermission permission="pro:read" />,
            children: [
              { path: 'pro', element: <Navigate to="/pro/cases" replace /> },
              { path: 'pro/cases', element: <ProBoardPage /> },
              { path: 'pro/cases/:id', element: <ProCasePage /> },
              { path: 'pro/processes', element: <ProTemplatesPage /> },
              { path: 'pro/reports', element: <ProReportsPage /> },
            ],
          },

          { path: '404', element: <NotFoundPage /> },
          { path: '*', element: <Navigate to="/404" replace /> },
        ],
      },
    ],
  },
])
